/*  src/components/LayoutEditor.js  */
/*  ------------------------------------------------------------------ */
import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Button, Box, Tooltip, Typography, Divider,
  TextField, Paper, Grid, FormControl, InputLabel, Select, MenuItem,
  ClickAwayListener
} from '@mui/material';
import AddIcon             from '@mui/icons-material/Add';
import DeleteIcon          from '@mui/icons-material/Delete';
import ContentCopy         from '@mui/icons-material/ContentCopy';
import TextFieldsIcon      from '@mui/icons-material/TextFields';
import EditIcon            from '@mui/icons-material/Edit';
import EditOffIcon         from '@mui/icons-material/EditOff';
import CloseIcon           from '@mui/icons-material/Close';
// Improved icons for actions
import RestoreIcon         from '@mui/icons-material/Restore';        // For resetting to defaults (was RefreshIcon)
import SaveAltIcon         from '@mui/icons-material/SaveAlt';         // For downloading JSON (was SaveIcon)
import FileUploadIcon      from '@mui/icons-material/FileUpload';      // For loading JSON file (was UploadFileIcon)
import DoneAllIcon         from '@mui/icons-material/DoneAll';         // For applying changes (was PublishIcon)
import EventNoteIcon from '@mui/icons-material/EventNote';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import PlaceIcon from '@mui/icons-material/Place';

import PhotoBookLayout           from './PhotoBook/PhotoBookLayout';
import { layouts as globalAppLayouts, loadLayouts as reloadGlobalLayoutsFromJson, findMatchingDefaultStyle, defaultStyles, processLayout } from './PhotoBook/layouts';
import useLazyBackgroundImages from '../hooks/useLazyBackgroundImages';

// Helper function to create a new photo placeholder
const makeNewPlaceholder = (pageIdx) => ({
  page: pageIdx,
  idxOnPage: 0,
  globalIdx: 0, // will be re-indexed later
  width: '30%',
  aspectRatio: '4:3',
  top: '5%',
  left: '5%',
  rotation: '0deg'
});

/* ----- helpers ------------------------------------------------------ */
const randImg = (seed) => `https://picsum.photos/seed/${seed}/1200/900`;

const DEFAULT_TEXT_BOX = {
  style    : {
    top:'70%', left:'15%',
    width:'70%', height:'20%',
    fontSize:'18px',
    fontFamily:'Georgia, serif',
    backgroundColor:'rgba(255,255,255,0.6)',
    padding:'4px',
    borderRadius:'6px',
    lineHeight:'1.35',
    textAlign:'left',
    color:'#000'
  }
};

// Helper to get details for different text-based element types
const getElementTypeDetails = (type) => {
  switch (type) {
    case 'textbox': 
      return { title: 'Edit Text Box', key: 'text', defaultInstance: JSON.parse(JSON.stringify(DEFAULT_TEXT_BOX)) };
    case 'dateCity': 
      return { title: 'Edit Date/City', key: 'dateCityLocation', defaultInstance: JSON.parse(JSON.stringify(DEFAULT_TEXT_BOX)) };
    case 'pageNumber': 
      return { title: 'Edit Page Number', key: 'pageNumberLocation', defaultInstance: JSON.parse(JSON.stringify(DEFAULT_TEXT_BOX)) };
    case 'placeName': 
      return { title: 'Edit Place Name', key: 'placeName', defaultInstance: JSON.parse(JSON.stringify(DEFAULT_TEXT_BOX)) };
    default: 
      return { title: 'Edit Element', key: null, defaultInstance: JSON.parse(JSON.stringify(DEFAULT_TEXT_BOX)) };
  }
};

/* ----- component ---------------------------------------------------- */
// Placeholder Attribute Editor Component
const PlaceholderAttributeEditor = ({ layoutIdx, phIdx, working, setWorking, updatePlaceholder, delPlaceholder, onClose }) => {
  const layout = working[layoutIdx];
  const placeholder = (() => {
    const leftPlaceholders = layout?.leftPage?.photoPlaceholders || [];
    const rightPlaceholders = layout?.rightPage?.photoPlaceholders || [];
    const allPlaceholders = [...leftPlaceholders, ...rightPlaceholders];
    return allPlaceholders[phIdx] || null;
  })();
  
  // Initial form values from the placeholder directly
  const [formValues, setFormValues] = useState({
    width: placeholder?.width || '30%',
    aspectRatio: placeholder?.aspectRatio || '4:3',
    top: placeholder?.top || '0%',
    left: placeholder?.left || '0%',
    rotation: placeholder?.rotation || '0deg'
  });
  
  // Handle input changes
  const handleChange = (prop) => (event) => {
    setFormValues({ ...formValues, [prop]: event.target.value });
  };
  
  // Apply changes
  const applyChanges = () => {
    // Update the placeholder object directly in workingLayouts
    setWorking(arr => {
      const n = JSON.parse(JSON.stringify(arr));
      const layoutToUpdate = n[layoutIdx];
      if (!layoutToUpdate) return arr; // Should not happen

      const leftPlaceholders = layoutToUpdate.leftPage?.photoPlaceholders || [];
      const rightPlaceholders = layoutToUpdate.rightPage?.photoPlaceholders || [];
      const allPlaceholders = [...leftPlaceholders, ...rightPlaceholders];
      const ph = allPlaceholders[phIdx];
      
      if (ph) {
        ph.width = formValues.width;
        ph.aspectRatio = formValues.aspectRatio;
        ph.rotation = formValues.rotation;
        ph.left = formValues.left;
        ph.top = formValues.top;
      }
      return n;
    });
    
    onClose();
  };
  
  if (!placeholder) return <Typography>Placeholder not found</Typography>;
  
  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Width"
            fullWidth
            value={formValues.width}
            onChange={handleChange('width')}
            helperText="e.g., 30%, 200px"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Aspect Ratio"
            fullWidth
            value={formValues.aspectRatio}
            onChange={handleChange('aspectRatio')}
            helperText="e.g., 4:3, 16:9, 1:1"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Top Position"
            fullWidth
            value={formValues.top}
            onChange={handleChange('top')}
            helperText="e.g., 10%, 50px"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Left Position"
            fullWidth
            value={formValues.left}
            onChange={handleChange('left')}
            helperText="e.g., 15%, 100px"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Rotation"
            fullWidth
            value={formValues.rotation}
            onChange={handleChange('rotation')}
            helperText="e.g., 0deg, 45deg, -10deg"
          />
        </Grid>
      </Grid>
      
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={onClose} sx={{ mr: 1 }}>Cancel</Button>
        <Button color="error" onClick={() => { delPlaceholder(layoutIdx, phIdx); onClose(); }} sx={{ mr: 1 }}>Delete</Button>
        <Button variant="contained" onClick={applyChanges}>Apply Changes</Button>
      </Box>
    </>
  );
};

// Text Box Attribute Editor Component (Generalized)
/* Updated TextBoxAttributeEditor: position & style fields now sync directly to elementData.style */
const TextBoxAttributeEditor = ({ layoutIdx, pageIdx, elementType, working, setWorking, deleteElement, onClose }) => {
  const layout = working[layoutIdx];
  const { key: elementKey, defaultInstance } = getElementTypeDetails(elementType);
  const page = pageIdx === 0 ? layout?.leftPage : layout?.rightPage;
  const elementData = page?.[elementKey] || defaultInstance;

  // Determine which style keys control vertical & horizontal positioning
  const verticalKey = (elementData.style?.bottom !== undefined && elementData.style.top === undefined)
    ? 'bottom'
    : 'top';
  const horizontalKey = (elementData.style?.right !== undefined && elementData.style.left === undefined)
    ? 'right'
    : 'left';

  // Initial values from style or fallback to position or blank
  const initialVertical = elementData.style?.[verticalKey] ?? elementData.position?.[verticalKey] ?? '';
  const initialHorizontal = elementData.style?.[horizontalKey] ?? elementData.position?.[horizontalKey] ?? '';

  // Extract default style and any other overrides
  const { defaultName: initialDefaultName, remainingStyle: initialOverrides } =
    findMatchingDefaultStyle(elementData.style || defaultInstance.style);

  // Form state: verticalKey, horizontalKey, plus overrides
  const [selectedDefaultStyle, setSelectedDefaultStyle] = useState(initialDefaultName);
  const [formValues, setFormValues] = useState({
    [verticalKey]: initialVertical,
    [horizontalKey]: initialHorizontal,
    width: initialOverrides.width || '',
    height: initialOverrides.height || '',
    fontSize: initialOverrides.fontSize || '',
    fontFamily: initialOverrides.fontFamily || '',
    backgroundColor: initialOverrides.backgroundColor || '',
    padding: initialOverrides.padding || '',
    borderRadius: initialOverrides.borderRadius || '',
    lineHeight: initialOverrides.lineHeight || '',
    textAlign: initialOverrides.textAlign || '',
    color: initialOverrides.color || '',
    textShadow: initialOverrides.textShadow || '',
  });

  const handleChange = (prop) => (e) => {
    setFormValues(v => ({ ...v, [prop]: e.target.value }));
  };

  const handleDefaultStyleChange = (e) => {
    setSelectedDefaultStyle(e.target.value);
    // clear overrides when switching default
    setFormValues(v => ({
      ...v,
      width: '', height: '', fontSize: '', fontFamily: '',
      backgroundColor: '', padding: '', borderRadius: '',
      lineHeight: '', textAlign: '', color: '', textShadow: ''
    }));
  };

  const applyChanges = () => {
    setWorking(arr => {
      const n = JSON.parse(JSON.stringify(arr));
      const targetPage = pageIdx === 0 ? n[layoutIdx].leftPage : n[layoutIdx].rightPage;

      // Ensure element exists
      if (!targetPage[elementKey]) targetPage[elementKey] = JSON.parse(JSON.stringify(defaultInstance));

      // Build style object with expanded default style
      let style = {};
      
      // First, if a default style is selected, expand it into the style object
      if (selectedDefaultStyle && selectedDefaultStyle !== 'none') {
        // Get the actual default style content
        const defaultStyleContent = defaultStyles[selectedDefaultStyle];
        if (defaultStyleContent) {
          // Start with the full default style
          style = { ...defaultStyleContent };
          // Also keep track of which default was used (for export optimization)
          style.default = selectedDefaultStyle;
        } else {
          // Fallback if somehow the selected style doesn't exist
          style.default = selectedDefaultStyle;
        }
      }
      
      // Add position values to style (top/bottom, left/right)
      if (formValues[verticalKey] && formValues[verticalKey].trim() !== '') {
        style[verticalKey] = formValues[verticalKey];
      }
      if (formValues[horizontalKey] && formValues[horizontalKey].trim() !== '') {
        style[horizontalKey] = formValues[horizontalKey];
      }
      
      // Now add overrides only for non-empty values
      for (const key of ['width','height','fontSize','fontFamily','backgroundColor','padding','borderRadius','lineHeight','textAlign','color','textShadow']) {
        if (formValues[key] && formValues[key].trim() !== '') {
          style[key] = formValues[key];
        }
      }
      
      targetPage[elementKey].style = style;
      
      // Position is now fully in style, so we can remove the separate position property
      if (targetPage[elementKey].position) {
        delete targetPage[elementKey].position;
      }

      return n;
    });
    onClose();
  };

  return (
    <>
      <Grid container spacing={2}>
        {/* Position fields: label dynamically based on key */}
        <Grid item xs={6}>
          <TextField
            label={verticalKey.charAt(0).toUpperCase() + verticalKey.slice(1)}
            fullWidth
            value={formValues[verticalKey]}
            onChange={handleChange(verticalKey)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label={horizontalKey.charAt(0).toUpperCase() + horizontalKey.slice(1)}
            fullWidth
            value={formValues[horizontalKey]}
            onChange={handleChange(horizontalKey)}
          />
        </Grid>

        {/* Default Style Selector */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Default Style</InputLabel>
            <Select
              value={selectedDefaultStyle || 'none'}
              onChange={handleDefaultStyleChange}
            >
              <MenuItem value="none">None (Custom)</MenuItem>
              {Object.keys(defaultStyles).map(name => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Style Overrides: blank implies default */}
        {['width','height','fontSize','fontFamily','backgroundColor','padding','borderRadius','lineHeight','textAlign','color','textShadow'].map(key => (
          <Grid item xs={6} key={key}>
            <TextField
              label={key.charAt(0).toUpperCase() + key.slice(1)}
              fullWidth
              value={formValues[key]}
              onChange={handleChange(key)}
              helperText={key === 'textShadow' ? 'e.g., 1px 1px 2px rgba(0,0,0,0.7)' : (formValues[key] ? '' : 'Using default')}
            />
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt:3, display:'flex', justifyContent:'space-between' }}>
        <Button color="error" onClick={() => { deleteElement(layoutIdx,pageIdx,elementType); onClose(); }}>
          Delete
        </Button>
        <Box>
          <Button onClick={onClose} sx={{ mr:1 }}>Cancel</Button>
          <Button variant="contained" onClick={applyChanges}>Apply</Button>
        </Box>
      </Box>
    </>
  );
};

export default function LayoutEditor({ onClose }) {

  /* ---------------- state ---------------- */
  const [workingLayouts, setWorkingLayouts] = useState([]);
  const [isEditMode, setIsEditMode] = useState(true);
  const [isDraggingElement, setIsDraggingElement] = useState(false);
  const nextId = useRef(Date.now());
  const fileInputRef = useRef(null); // Reference for the hidden file input
  
  // State for selected element attribute editor
  const [selectedElement, setSelectedElement] = useState(null);
  // null = no selection, { type: 'placeholder', layoutIdx, phIdx } 
  // or { type: 'textbox' | 'dateCity' | 'pageNumber' | 'placeName', layoutIdx, pageIdx, elementType? (not needed if type is specific) }
  
  // Initialize lazy loading for background images
  useLazyBackgroundImages();

  // Handler for triggering the file input click
  const handleLoadJsonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handler for processing the uploaded JSON file
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result);
        if (Array.isArray(jsonData)) {
          // Process each layout to expand default styles before setting as working layouts
          const processedLayouts = jsonData.map(layout => processLayout(layout));
          setWorkingLayouts(processedLayouts);
          console.log(`Loaded ${processedLayouts.length} layouts from uploaded JSON file`);
        } else {
          console.error('Invalid JSON format: Expected an array of layouts');
          alert('Invalid JSON format: Expected an array of layouts');
        }
      } catch (error) {
        console.error('Error parsing JSON file:', error);
        alert('Error parsing JSON file. Please ensure it is a valid JSON file.');
      }
    };
    reader.readAsText(file);
    // Reset the file input value so the same file can be selected again if needed
    event.target.value = null;
  };

  const initializeWorkingLayouts = async () => {
    // Ensure global layouts are loaded if not already
    if (globalAppLayouts.length === 0) {
      await reloadGlobalLayoutsFromJson();
    }
    // Deep copy to prevent direct mutation of global state
    const processedLayouts = JSON.parse(JSON.stringify(globalAppLayouts));
    setWorkingLayouts(processedLayouts);
    // console.log('Initialized working layouts:', processedLayouts);
  };

  useEffect(() => {
    initializeWorkingLayouts();
  }, []);

  // Helper to process styles for export
  const optimizeStyleForExport = (styleObject) => {
    if (!styleObject || typeof styleObject !== 'object') return styleObject;
    const { defaultName, remainingStyle } = findMatchingDefaultStyle(styleObject);
    if (defaultName) {
      // If remainingStyle is empty, only the default key is needed.
      // Otherwise, include the default key and the remaining styles.
      const exportedStyle = { default: defaultName };
      for (const key in remainingStyle) {
        if (Object.prototype.hasOwnProperty.call(remainingStyle, key)) {
          exportedStyle[key] = remainingStyle[key];
        }
      }
      // If exportedStyle only has `default` key and no overrides, fine.
      // If remainingStyle was truly empty, then it only contains `default`.
      return exportedStyle;
    }
    return styleObject; // No default match, return original (or already processed)
  };

  const download = () => {
    // Create a deep copy to modify for export
    const layoutsToExport = JSON.parse(JSON.stringify(workingLayouts));

    // Iterate through layouts and optimize styles
    const optimizedLayouts = layoutsToExport.map(layout => {
      const newLayout = { ...layout };

      // Process left page elements
      if (newLayout.leftPage) {
        const leftPage = newLayout.leftPage;
        if (leftPage.text?.style) {
          // console.log(`[LayoutEditor.js] Left Page - text - Style before optimize:`, JSON.parse(JSON.stringify(leftPage.text.style)));
          leftPage.text.style = optimizeStyleForExport(leftPage.text.style);
          // console.log(`[LayoutEditor.js] Left Page - text - Style after optimize:`, leftPage.text.style);
        }
        if (leftPage.dateCityLocation?.style) {
          leftPage.dateCityLocation.style = optimizeStyleForExport(leftPage.dateCityLocation.style);
        }
        if (leftPage.placeName?.style) {
          leftPage.placeName.style = optimizeStyleForExport(leftPage.placeName.style);
        }
        if (leftPage.pageNumberLocation?.style) {
          leftPage.pageNumberLocation.style = optimizeStyleForExport(leftPage.pageNumberLocation.style);
        }
        if (leftPage.scatteredWordPlaceholders) {
          leftPage.scatteredWordPlaceholders = leftPage.scatteredWordPlaceholders.map(ph => ({
            ...ph,
            style: ph.style ? optimizeStyleForExport(ph.style) : undefined
          }));
        }
      }

      // Process right page elements
      if (newLayout.rightPage) {
        const rightPage = newLayout.rightPage;
        if (rightPage.text?.style) {
          // console.log(`[LayoutEditor.js] Right Page - text - Style before optimize:`, JSON.parse(JSON.stringify(rightPage.text.style)));
          rightPage.text.style = optimizeStyleForExport(rightPage.text.style);
          // console.log(`[LayoutEditor.js] Right Page - text - Style after optimize:`, rightPage.text.style);
        }
        if (rightPage.dateCityLocation?.style) {
          rightPage.dateCityLocation.style = optimizeStyleForExport(rightPage.dateCityLocation.style);
        }
        if (rightPage.placeName?.style) {
          rightPage.placeName.style = optimizeStyleForExport(rightPage.placeName.style);
        }
        if (rightPage.pageNumberLocation?.style) {
          rightPage.pageNumberLocation.style = optimizeStyleForExport(rightPage.pageNumberLocation.style);
        }
        if (rightPage.scatteredWordPlaceholders) {
          rightPage.scatteredWordPlaceholders = rightPage.scatteredWordPlaceholders.map(ph => ({
            ...ph,
            style: ph.style ? optimizeStyleForExport(ph.style) : undefined
          }));
        }
      }
      return newLayout;
    });

    const data = JSON.stringify(optimizedLayouts, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'layouts.json';
    link.click();
    URL.revokeObjectURL(url);
    // console.log('Downloaded layouts:', optimizedLayouts);
  };

  /* ----- global ops ---------------------- */
  // Note: We've removed the applyModificationsToLayouts function as we now directly update the placeholders
  
  const addBlankLayout = () => {
    setWorkingLayouts(arr => [...arr, {
      id        : `new-${nextId.current++}`,
      leftPage  : { photoPlaceholders: [] },
      rightPage : { photoPlaceholders: [] },
      background: '#f7f7f7'
    }]);
  };

  // This function reloads the editor's "working" copy from the master "globalAppLayouts"
  // or re-fetches from JSON if globalAppLayouts is somehow still empty.
  const resetWorkingToAppDefault = async () => {
    if (globalAppLayouts.length > 0) {
        setWorkingLayouts(JSON.parse(JSON.stringify(globalAppLayouts)));
        console.log("LayoutEditor: Reset working layouts from globalAppLayouts.");
    } else {
        console.warn("LayoutEditor: Resetting, but globalAppLayouts is empty. Re-fetching from JSON.");
        const loaded = await reloadGlobalLayoutsFromJson();
        setWorkingLayouts(JSON.parse(JSON.stringify(loaded)));
    }
  };
  
  // Applies the editor's "working" layouts to the shared "globalAppLayouts"
  // This makes the changes available to the rest of the app in the current session.
  const applyWorkingLayoutsToGlobal = () => {
    // Since we now directly update the placeholders, we can use workingLayouts directly
    const finalLayouts = JSON.parse(JSON.stringify(workingLayouts));
    
    globalAppLayouts.length = 0; // Clear the global array
    // Push the finalized layouts to the global array
    finalLayouts.forEach(layout => globalAppLayouts.push(layout));
    console.log("LayoutEditor: Applied working layouts to globalAppLayouts. These will be used for new storybooks in this session.");
    alert("Layouts applied to current session. New storybooks will use these modified layouts.");
  };

  /* ----- per-layout helpers -------------- */
  const duplicate = idx =>
    setWorkingLayouts(arr => {
      const copy = JSON.parse(JSON.stringify(arr[idx]));
      copy.id = `dup-${nextId.current++}`;
      return [...arr.slice(0,idx+1), copy, ...arr.slice(idx+1)];
    });

  const remove = idx =>
    setWorkingLayouts(arr => arr.length===1 ? arr : arr.filter((_,i)=>i!==idx));

  const reindexPlaceholders = lay => {
    if (!lay) return; // Add guard for undefined lay
    const l = lay.leftPage?.photoPlaceholders ?? [];
    const r = lay.rightPage?.photoPlaceholders ?? [];
    [...l,...r].forEach((ph,i)=>{ ph.globalIdx=i; });
  };

  const addPh = (layoutIdx,pageIdx) =>
    setWorkingLayouts(arr => {
      const n = JSON.parse(JSON.stringify(arr));
      const tgt = n[layoutIdx];
      (pageIdx===0 ? tgt.leftPage.photoPlaceholders
                   : tgt.rightPage.photoPlaceholders).push(makeNewPlaceholder(pageIdx));
      reindexPlaceholders(tgt);
      return n;
    });

  const addTxt = (layoutIdx,pageIdx) =>
    setWorkingLayouts(arr=>{
      const n=JSON.parse(JSON.stringify(arr));
      const page = pageIdx===0 ? n[layoutIdx].leftPage : n[layoutIdx].rightPage;
      if(!page.text) page.text=JSON.parse(JSON.stringify(DEFAULT_TEXT_BOX));
      return n;
    });

  // Generalized function to delete various text-based elements from a page
  const delElementOnPage = (layoutIdx, pageIdx, elementType) => {
    setWorkingLayouts(arr => {
      const n = JSON.parse(JSON.stringify(arr));
      const page = pageIdx === 0 ? n[layoutIdx].leftPage : n[layoutIdx].rightPage;
      const { key: elementKey } = getElementTypeDetails(elementType);
      
      if (page && elementKey && page[elementKey]) {
        delete page[elementKey];
      }
      return n;
    });
  };

  const updatePlaceholder = (layoutIdx, phIdx, newOverrides) => {
    setWorkingLayouts(prevWorkingLayouts => {
      const newWorkingLayouts = JSON.parse(JSON.stringify(prevWorkingLayouts));
      const layoutToModify = newWorkingLayouts[layoutIdx];

      if (!layoutToModify) return prevWorkingLayouts; // Should not happen if layoutIdx is valid

      // Get all placeholders from both pages
      const leftPlaceholders = layoutToModify.leftPage?.photoPlaceholders || [];
      const rightPlaceholders = layoutToModify.rightPage?.photoPlaceholders || [];
      const allPlaceholders = [...leftPlaceholders, ...rightPlaceholders];
      
      // Find the placeholder to modify
      const placeholder = allPlaceholders[phIdx];
      if (!placeholder) return prevWorkingLayouts; // Should not happen if phIdx is valid
      
      // Apply the overrides directly to the placeholder
      if (newOverrides.widthOverride !== undefined) {
        placeholder.width = newOverrides.widthOverride;
      }
      if (newOverrides.aspectRatioOverride !== undefined) {
        placeholder.aspectRatio = newOverrides.aspectRatioOverride;
      }
      if (newOverrides.offsetX !== undefined || newOverrides.offsetY !== undefined) {
        // Calculate the new absolute position from the base + offset
        if (newOverrides.offsetX !== undefined) {
          // Extract numeric value and unit from original position
          const originalLeft = parseFloat(placeholder.left) || 0;
          const originalUnit = placeholder.left?.toString().replace(/[\d.-]/g, '') || '%';
          const offsetX = parseFloat(newOverrides.offsetX) || 0;
          placeholder.left = `${(originalLeft + offsetX).toFixed(2)}${originalUnit}`;
        }
        
        if (newOverrides.offsetY !== undefined) {
          const originalTop = parseFloat(placeholder.top) || 0;
          const originalUnit = placeholder.top?.toString().replace(/[\d.-]/g, '') || '%';
          const offsetY = parseFloat(newOverrides.offsetY) || 0;
          placeholder.top = `${(originalTop + offsetY).toFixed(2)}${originalUnit}`;
        }
      }
      
      return newWorkingLayouts;
    });
  }

  const delPlaceholder = (layoutIdx, phIdx) =>
  setWorkingLayouts(arr => {
    const n = JSON.parse(JSON.stringify(arr));
    const leftPlaceholders = n[layoutIdx]?.leftPage?.photoPlaceholders || [];
    // eslint-disable-next-line no-unused-vars
    const rightPlaceholders = n[layoutIdx]?.rightPage?.photoPlaceholders || [];
    
    // Determine which page the placeholder belongs to
    if (phIdx < leftPlaceholders.length) {
      // It's in the left page
      n[layoutIdx].leftPage.photoPlaceholders.splice(phIdx, 1);
    } else {
      // It's in the right page, adjust the index
      const rightIndex = phIdx - leftPlaceholders.length;
      n[layoutIdx].rightPage.photoPlaceholders.splice(rightIndex, 1);
    }
    
    reindexPlaceholders(n[layoutIdx]);
    return n;
  });

  /* ---------------- render ---------------- */
  return (
    <Dialog open fullScreen>
      <DialogTitle sx={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <Typography variant="h6" component="span">Layout Editor</Typography>
        <Box>
          {/* Hidden file input */}
          <input
            type="file"
            accept=".json"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
          <Tooltip title="Toggle Edit Mode (Placeholder Drag/Resize)">
            <IconButton 
              onClick={() => setIsEditMode(!isEditMode)}
              color={isEditMode ? "primary" : "default"}
            >
              {isEditMode ? <EditOffIcon /> : <EditIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Reset Editor to App's Default Layouts">
            <IconButton onClick={resetWorkingToAppDefault}><RestoreIcon/></IconButton>
          </Tooltip>
          <Tooltip title="Add New Blank Layout to Editor">
            <IconButton onClick={addBlankLayout}><AddIcon/></IconButton>
          </Tooltip>
          <Tooltip title="Apply Editor's Layouts to Current App Session">
            <IconButton onClick={applyWorkingLayoutsToGlobal}><DoneAllIcon/></IconButton>
          </Tooltip>
          <Tooltip title="Load Layouts from JSON File">
            <IconButton onClick={handleLoadJsonClick}><FileUploadIcon/></IconButton>
          </Tooltip>
          <Tooltip title="Download Editor's Layouts as JSON">
            <IconButton onClick={download}><SaveAltIcon/></IconButton>
          </Tooltip>
          <Button onClick={onClose}>Close</Button>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{bgcolor:'#fafafa'}}>
        {workingLayouts.map((lay,idx)=>{
          /* how many photos are needed? */
          const phCnt = (lay.leftPage?.photoPlaceholders?.length||0)+(lay.rightPage?.photoPlaceholders?.length||0);
          const dummyImgs = Array.from({length:phCnt},(_,i)=>randImg(`${idx}-${i}`));

          /* dummy texts if original has text box */
          const lText = lay.leftPage?.text  ? 'Lorem ipsum dolor sit amet…'  : '';
          const rText = lay.rightPage?.text ? 'Sed ut perspiciatis unde…'    : '';

          const currentDate = new Date().toLocaleDateString();
          const dummyCity = 'Sample City';
          const dummyScatteredWords = ['Adventure', 'Explore', 'Discover', 'Journey', 'Memories', 'Escape', 'Wander', 'Roam', 'Venture', 'Odyssey'];

          return (
            <Box key={lay.id} sx={{mb:4,p:1,position:'relative',border:'1px solid #ddd'}}>
              {/* per-layout toolbar */}
              <Box sx={{position:'absolute',top:8,right:8,zIndex:30,display:'flex',gap:1,background:'rgba(255,255,255,0.8)',borderRadius:1}}>
                <Tooltip title="duplicate layout"><IconButton size="small" onClick={()=>duplicate(idx)}><ContentCopy fontSize="small"/></IconButton></Tooltip>
                <Tooltip title="delete layout"><IconButton size="small" onClick={()=>remove(idx)}><DeleteIcon fontSize="small"/></IconButton></Tooltip>
                <Divider orientation="vertical" flexItem />
                <Tooltip title="add photo L"><IconButton size="small" onClick={()=>addPh(idx,0)}><AddIcon fontSize="inherit"/>L</IconButton></Tooltip>
                <Tooltip title="add photo R"><IconButton size="small" onClick={()=>addPh(idx,1)}><AddIcon fontSize="inherit"/>R</IconButton></Tooltip>
                <Tooltip title="add text L"><IconButton size="small" onClick={()=>addTxt(idx,0)}><TextFieldsIcon fontSize="inherit"/>L</IconButton></Tooltip>
                <Tooltip title="add text R"><IconButton size="small" onClick={()=>addTxt(idx,1)}><TextFieldsIcon fontSize="inherit"/>R</IconButton></Tooltip>
                <Divider orientation="vertical" flexItem />
                <Tooltip title="Edit Date/City L"><IconButton size="small" onClick={() => setSelectedElement({ type: 'dateCity', layoutIdx: idx, pageIdx: 0 })}><EventNoteIcon fontSize="inherit"/>L</IconButton></Tooltip>
                <Tooltip title="Edit Date/City R"><IconButton size="small" onClick={() => setSelectedElement({ type: 'dateCity', layoutIdx: idx, pageIdx: 1 })}><EventNoteIcon fontSize="inherit"/>R</IconButton></Tooltip>
                <Tooltip title="Edit Page Number L"><IconButton size="small" onClick={() => setSelectedElement({ type: 'pageNumber', layoutIdx: idx, pageIdx: 0 })}><FormatListNumberedIcon fontSize="inherit"/>L</IconButton></Tooltip>
                <Tooltip title="Edit Page Number R"><IconButton size="small" onClick={() => setSelectedElement({ type: 'pageNumber', layoutIdx: idx, pageIdx: 1 })}><FormatListNumberedIcon fontSize="inherit"/>R</IconButton></Tooltip>
                <Tooltip title="Edit Place Name L"><IconButton size="small" onClick={() => setSelectedElement({ type: 'placeName', layoutIdx: idx, pageIdx: 0 })}><PlaceIcon fontSize="inherit"/>L</IconButton></Tooltip>
                <Tooltip title="Edit Place Name R"><IconButton size="small" onClick={() => setSelectedElement({ type: 'placeName', layoutIdx: idx, pageIdx: 1 })}><PlaceIcon fontSize="inherit"/>R</IconButton></Tooltip>
              </Box>

              <PhotoBookLayout
                key={lay.id || `photobook-layout-${idx}`}
                layout={lay}
                images={dummyImgs}
                photoObjects={[]} /* no real photo objects */
                leftPageText={lText}
                rightPageText={rText}
                leftPageDisplayDate={currentDate}
                rightPageDisplayDate={currentDate}
                leftPageDisplayCity={dummyCity}
                rightPageDisplayCity={dummyCity}
                leftPageData={{ textElements: lay.leftPage?.scatteredWordPlaceholders?.map((_, i) => ({
                  content: dummyScatteredWords[i % dummyScatteredWords.length],
                  style: { ...lay.leftPage.scatteredWordPlaceholders[i]?.style || {} }
                })) || [] }}
                rightPageData={{ textElements: lay.rightPage?.scatteredWordPlaceholders?.map((_, i) => ({
                  content: dummyScatteredWords[(i + 3) % dummyScatteredWords.length],
                  style: { ...lay.rightPage.scatteredWordPlaceholders[i]?.style || {} }
                })) || [] }}
                // provide dummy names so placeName placeholders appear
                photo_place_names={dummyImgs.map((_, i) => `Photo ${i + 1}`)}
                // assign sequential page numbers for preview
                leftPageNumber={idx * 2 + 1}
                rightPageNumber={idx * 2 + 2}
                isPrintLayout={false}
                isPlaceholderEditingActive={isEditMode}
                isTextPlaceholderEditingActive={isEditMode}
                placeholderEditData={{}}
                onPlaceholderUpdate={(phIdx,newOv)=>updatePlaceholder(idx,phIdx,newOv)}
                onPlaceholderSelect={(phIdx) => setSelectedElement({ type: 'placeholder', layoutIdx: idx, phIdx })}
                onTextPlaceholderUpdate={(pageIdx, changes) => {
                  setWorkingLayouts(arr => {
                    const n = JSON.parse(JSON.stringify(arr));
                    const page = pageIdx === 0 ? n[idx].leftPage : n[idx].rightPage;
                    page.text = {
                      ...page.text,
                      style: { ...page.text.style, ...changes.style }
                    };
                    return n;
                  });
                }}
                onTextPlaceholderSelect={(pageIdx) => setSelectedElement({ type: 'textbox', layoutIdx: idx, pageIdx })}
                onElementDragStart={() => setIsDraggingElement(true)}
                onElementDragEnd={() => {
                  setIsDraggingElement(false);
                  //setSelectedElement(null);
                }}
              />
            </Box>
          );
        })}
      </DialogContent>

      <DialogActions>
        <Typography sx={{flexGrow:1,ml:2}}>Layouts in Editor: {workingLayouts.length}</Typography>
      </DialogActions>
      
      {/* Attribute Editor Overlay */}
      {selectedElement && isEditMode && !isDraggingElement && (
        <ClickAwayListener onClickAway={() => setSelectedElement(null)}>
          <Paper 
            elevation={8} 
            sx={{
              position: 'fixed',
              right: 20,
              top: 80,
              width: 400,
              maxHeight: 'calc(100vh - 160px)',
              overflow: 'auto',
              padding: 3,
              zIndex: 9999,
              backgroundColor: '#fff',
              borderRadius: 2,
            }}
          >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {selectedElement.type === 'placeholder' 
                ? 'Edit Photo Placeholder' 
                : getElementTypeDetails(selectedElement.type).title || 'Edit Element'}
            </Typography>
            <IconButton onClick={() => setSelectedElement(null)}>
              <CloseIcon />
            </IconButton>
          </Box>
          
          {selectedElement.type === 'placeholder' && (
            <PlaceholderAttributeEditor 
              layoutIdx={selectedElement.layoutIdx}
              phIdx={selectedElement.phIdx}
              working={workingLayouts}
              setWorking={setWorkingLayouts}
              updatePlaceholder={updatePlaceholder}
              delPlaceholder={delPlaceholder}
              onClose={() => setSelectedElement(null)}
            />
          )}
          
          {(selectedElement.type === 'textbox' || 
            selectedElement.type === 'dateCity' ||
            selectedElement.type === 'pageNumber' ||
            selectedElement.type === 'placeName') && (
            <TextBoxAttributeEditor 
              layoutIdx={selectedElement.layoutIdx}
              pageIdx={selectedElement.pageIdx}
              elementType={selectedElement.type}
              working={workingLayouts}
              setWorking={setWorkingLayouts}
              deleteElement={delElementOnPage} // Changed from delText to delElementOnPage
              onClose={() => setSelectedElement(null)}
            />
          )}
          </Paper>
        </ClickAwayListener>
      )}
    </Dialog>
  );
}
